import { useEffect, useState } from "react";
import clsx from "clsx";
import { get } from "@/services/api/client";

type MongoTarget = "staging" | "qa" | "production" | "unknown";

export interface ConnectionInfo {
  service: string;
  mongo_target: MongoTarget;
  mongo_host: string;
  mongo_database: string;
}

const TARGET_SHORT: Record<MongoTarget, string> = {
  staging: "STG",
  qa: "QA",
  production: "PROD",
  unknown: "?",
};

const TARGET_COLORS: Record<MongoTarget, string> = {
  staging: "text-amber-400",
  qa: "text-green-400",
  production: "text-red-400",
  unknown: "text-slate-400",
};

function normalizeTarget(value: string | undefined): MongoTarget {
  if (
    value === "staging" ||
    value === "qa" ||
    value === "production" ||
    value === "unknown"
  ) {
    return value;
  }
  return "unknown";
}

async function fetchWorkerConnectionInfo(): Promise<ConnectionInfo> {
  const response = await fetch("/worker-health/debug/connection-info");
  if (!response.ok) {
    throw new Error(`Worker health returned ${response.status}`);
  }
  return response.json() as Promise<ConnectionInfo>;
}

function buildTooltip(
  apiInfo: ConnectionInfo | null,
  workerInfo: ConnectionInfo | null,
  workerUnavailable: boolean,
): string {
  const apiLine = apiInfo
    ? `API: ${apiInfo.mongo_database} @ ${apiInfo.mongo_host}`
    : "API: unavailable";
  const workerLine = workerUnavailable
    ? "Worker: unavailable"
    : workerInfo
      ? `Worker: ${workerInfo.mongo_database} @ ${workerInfo.mongo_host}`
      : "Worker: loading";
  return `${apiLine}\n${workerLine}`;
}

function TargetBadge({
  label,
  target,
  pending,
}: {
  label: string;
  target: MongoTarget | null;
  pending?: boolean;
}) {
  const short = pending ? "…" : target ? TARGET_SHORT[target] : "—";

  return (
    <span>
      {label}{" "}
      <span className={clsx("font-semibold", pending || !target ? "text-slate-400" : TARGET_COLORS[target])}>
        {short}
      </span>
    </span>
  );
}

export function LocalDevMongoBanner() {
  if (!import.meta.env.DEV) {
    return null;
  }
  return <LocalDevMongoBannerInner />;
}

function LocalDevMongoBannerInner() {
  const [apiInfo, setApiInfo] = useState<ConnectionInfo | null>(null);
  const [workerInfo, setWorkerInfo] = useState<ConnectionInfo | null>(null);
  const [workerUnavailable, setWorkerUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const api = await get<ConnectionInfo>("/debug/connection-info");
        if (!cancelled) {
          setApiInfo(api);
        }
      } catch {
        if (!cancelled) {
          setApiInfo(null);
        }
      }

      try {
        const worker = await fetchWorkerConnectionInfo();
        if (!cancelled) {
          setWorkerInfo(worker);
          setWorkerUnavailable(false);
        }
      } catch {
        if (!cancelled) {
          setWorkerInfo(null);
          setWorkerUnavailable(true);
        }
      }
    }

    void load();
    const interval = setInterval(load, 60_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const apiTarget = apiInfo ? normalizeTarget(apiInfo.mongo_target) : null;
  const workerTarget = workerUnavailable
    ? null
    : workerInfo
      ? normalizeTarget(workerInfo.mongo_target)
      : null;

  const mismatch =
    apiTarget &&
    workerTarget &&
    apiTarget !== workerTarget;

  const hasProd = apiTarget === "production" || workerTarget === "production";

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="local-dev-mongo-banner"
      title={buildTooltip(apiInfo, workerInfo, workerUnavailable)}
      className={clsx(
        "pointer-events-none fixed bottom-3 right-3 z-50 rounded-full border px-2.5 py-1 text-[10px] font-medium shadow-sm backdrop-blur-sm",
        mismatch
          ? "border-red-500/60 bg-red-950/80 text-slate-300"
          : hasProd
            ? "border-red-500/40 bg-slate-900/80 text-slate-300"
            : "border-slate-600/60 bg-slate-900/80 text-slate-300",
      )}
    >
      <TargetBadge label="API" target={apiTarget} pending={!apiInfo} />
      <span className="text-slate-500"> · </span>
      <TargetBadge
        label="Wkr"
        target={workerTarget}
        pending={!workerUnavailable && !workerInfo}
      />
      {mismatch ? <span className="text-red-400"> !</span> : null}
    </div>
  );
}
